// ===========================================
// CANVAS PROTOTYPE DEMO PAGE
// ===========================================
// Test page for the canvas-based game table.
// Allows comparison with the current React DOM implementation.
// ===========================================

import { useState, useCallback, useRef } from 'react';
import { CanvasGameTable } from '../components/game/canvas';
import type { CanvasGameTableRef } from '../components/game/canvas/CanvasGameTable';
import type { Card as CardType, DealerState } from '@busted-blackjack/shared';

// ===========================================
// MOCK DATA
// ===========================================

type Rank = 'A' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9' | '10' | 'J' | 'Q' | 'K';
type Suit = 'hearts' | 'diamonds' | 'clubs' | 'spades';

const createCard = (rank: Rank, suit: Suit, faceUp: boolean = true): CardType => ({
  rank,
  suit,
  faceUp,
});

const MOCK_DEALER: DealerState = {
  cards: [
    createCard('K', 'spades', true),
    createCard('7', 'hearts', false),
  ],
  score: 17,
  visibleScore: 10,
  visibleCards: [createCard('K', 'spades', true)],
  isSoft: false,
  isBusted: false,
  isBlackjack: false,
  isStanding: false,
  holeCardRevealed: false,
};

const MOCK_DEALER_EMPTY: DealerState = {
  cards: [],
  score: 0,
  visibleScore: 0,
  visibleCards: [],
  isSoft: false,
  isBusted: false,
  isBlackjack: false,
  isStanding: false,
  holeCardRevealed: false,
};

interface MockPlayer {
  seatPosition: number;
  displayName: string;
  hands: Array<{
    cards: CardType[];
    bet: number;
    outcome?: string;
  }>;
  currentHandIndex: number;
}

const MOCK_PLAYERS: Map<number, MockPlayer> = new Map([
  [2, {
    seatPosition: 2,
    displayName: 'Alice',
    hands: [{
      cards: [createCard('A', 'hearts'), createCard('K', 'diamonds')],
      bet: 100,
    }],
    currentHandIndex: 0,
  }],
  [4, {
    seatPosition: 4,
    displayName: 'You',
    hands: [{
      cards: [createCard('9', 'clubs'), createCard('8', 'spades')],
      bet: 50,
    }],
    currentHandIndex: 0,
  }],
  [6, {
    seatPosition: 6,
    displayName: 'Bob',
    hands: [{
      cards: [createCard('5', 'hearts'), createCard('6', 'diamonds'), createCard('Q', 'clubs')],
      bet: 75,
    }],
    currentHandIndex: 0,
  }],
]);

// ===========================================
// COMPONENT
// ===========================================

export default function CanvasPrototypePage() {
  const canvasRef = useRef<CanvasGameTableRef>(null);
  const [mySeatPosition, setMySeatPosition] = useState<number | null>(null);
  const [players, setPlayers] = useState<Map<number, MockPlayer>>(new Map());
  const [dealer, setDealer] = useState<DealerState>(MOCK_DEALER_EMPTY);
  const [scenario, setScenario] = useState<'empty' | 'playing' | 'full'>('empty');

  // Handle seat click
  const handleSeatClick = useCallback((seatPosition: number) => {
    console.log('[CanvasPrototype] Seat clicked:', seatPosition);
    setMySeatPosition(seatPosition);

    // Add player to that seat
    setPlayers(prev => {
      const next = new Map(prev);
      next.set(seatPosition, {
        seatPosition,
        displayName: 'You',
        hands: [{
          cards: [createCard('7', 'hearts'), createCard('9', 'diamonds')],
          bet: 50,
        }],
        currentHandIndex: 0,
      });
      return next;
    });
  }, []);

  // Scenario buttons
  const loadEmptyTable = useCallback(() => {
    setScenario('empty');
    setMySeatPosition(null);
    setPlayers(new Map());
    setDealer(MOCK_DEALER_EMPTY);
  }, []);

  const loadPlayingScenario = useCallback(() => {
    setScenario('playing');
    setMySeatPosition(4);
    setPlayers(MOCK_PLAYERS);
    setDealer(MOCK_DEALER);
  }, []);

  const loadFullTable = useCallback(() => {
    setScenario('full');
    setMySeatPosition(4);

    const ranks: Rank[] = ['A', '2', '3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K'];
    const suits: Suit[] = ['hearts', 'diamonds', 'clubs', 'spades'];

    const fullPlayers = new Map<number, MockPlayer>();
    for (let i = 1; i <= 7; i++) {
      fullPlayers.set(i, {
        seatPosition: i,
        displayName: i === 4 ? 'You' : `Player ${i}`,
        hands: [{
          cards: [
            createCard(ranks[i % 13], suits[i % 4]),
            createCard(ranks[(i + 5) % 13], suits[(i + 1) % 4]),
          ],
          bet: 25 * i,
        }],
        currentHandIndex: 0,
      });
    }

    setPlayers(fullPlayers);
    setDealer(MOCK_DEALER);
  }, []);

  // Demo deal animation - deals cards one by one with timing
  const demoDealAnimation = useCallback(() => {
    if (!canvasRef.current) return;

    // Clear existing cards first
    setPlayers(new Map());
    setDealer(MOCK_DEALER_EMPTY);
    setMySeatPosition(4);

    const ranks: Rank[] = ['A', '2', '3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K'];
    const suits: Suit[] = ['hearts', 'diamonds', 'clubs', 'spades'];
    let cardCounter = 0;

    // Deal pattern: each player gets 1 card, then dealer, repeat
    const dealSequence: Array<{ type: 'player' | 'dealer'; seat?: number; cardIndex: number }> = [];

    // Round 1: First card to each seat
    for (let seat = 1; seat <= 7; seat++) {
      dealSequence.push({ type: 'player', seat, cardIndex: 0 });
    }
    dealSequence.push({ type: 'dealer', cardIndex: 0 });

    // Round 2: Second card to each seat
    for (let seat = 1; seat <= 7; seat++) {
      dealSequence.push({ type: 'player', seat, cardIndex: 1 });
    }
    dealSequence.push({ type: 'dealer', cardIndex: 1 });

    // Execute deal sequence with timing
    dealSequence.forEach((deal, index) => {
      setTimeout(() => {
        const rank = ranks[cardCounter % 13];
        const suit = suits[Math.floor(cardCounter / 13) % 4];
        const faceUp = deal.type === 'dealer' && deal.cardIndex === 1 ? false : true;
        const card = createCard(rank, suit, faceUp);
        cardCounter++;

        if (deal.type === 'player' && deal.seat !== undefined) {
          canvasRef.current?.dealCardToSeat(deal.seat, card, deal.cardIndex);
        } else {
          canvasRef.current?.dealCardToDealer(card, deal.cardIndex);
        }
      }, index * 150); // 150ms between each card
    });
  }, []);

  return (
    <div className="relative">
      {/* Scenario controls - floating panel */}
      <div className="fixed top-20 right-4 z-50 bg-gray-800 rounded-lg p-4 shadow-xl">
        <h3 className="text-white font-bold mb-3">Test Scenarios</h3>
        <div className="flex flex-col gap-2">
          <button
            onClick={loadEmptyTable}
            className={`px-4 py-2 rounded ${scenario === 'empty' ? 'bg-blue-600' : 'bg-gray-600'} text-white text-sm`}
          >
            Empty Table
          </button>
          <button
            onClick={loadPlayingScenario}
            className={`px-4 py-2 rounded ${scenario === 'playing' ? 'bg-blue-600' : 'bg-gray-600'} text-white text-sm`}
          >
            3 Players + Dealer
          </button>
          <button
            onClick={loadFullTable}
            className={`px-4 py-2 rounded ${scenario === 'full' ? 'bg-blue-600' : 'bg-gray-600'} text-white text-sm`}
          >
            Full Table (7 Players)
          </button>
        </div>

        <div className="mt-4 pt-4 border-t border-gray-600">
          <h4 className="text-white font-semibold mb-2">Animations</h4>
          <button
            onClick={demoDealAnimation}
            className="w-full px-4 py-2 rounded bg-green-600 hover:bg-green-500 text-white text-sm font-semibold"
          >
            Demo Deal Animation
          </button>
        </div>

        <div className="mt-4 pt-4 border-t border-gray-600">
          <p className="text-gray-400 text-xs">
            Current seat: {mySeatPosition ?? 'Spectator'}
          </p>
          <p className="text-gray-400 text-xs">
            Players: {players.size}
          </p>
        </div>
      </div>

      {/* Canvas game table */}
      <CanvasGameTable
        ref={canvasRef}
        dealer={dealer}
        players={players}
        mySeatPosition={mySeatPosition}
        onSeatClick={handleSeatClick}
      />
    </div>
  );
}
